<template>
  <!-- ICONIC: Einladungslinks für eine Rolle (TeamSpeak-Einladungsschlüssel, je einmal nutzbar) -->
  <v-container class="einladungen">
    <div class="d-flex flex-wrap align-end mb-4">
      <div class="mr-auto mb-2">
        <h1 class="text-h5 font-weight-medium">Einladungen</h1>
        <div class="text-body-2 text--secondary">
          Link für eine Rolle erzeugen und schicken. Jeder Link geht genau einmal.
        </div>
      </div>
      <v-btn color="primary" depressed class="mb-2" @click="openCreate">
        <v-icon left>mdi-plus</v-icon>Neue Einladung
      </v-btn>
    </div>

    <v-card outlined>
      <v-progress-linear v-if="loading" indeterminate></v-progress-linear>
      <p v-if="!loading && !keys.length" class="text-body-2 text--secondary pa-4 mb-0">
        Keine offenen Einladungen.
      </p>
      <v-list v-else class="py-0">
        <template v-for="(k, i) in keys">
          <v-divider v-if="i" :key="`d${k.token}`"></v-divider>
          <v-list-item :key="k.token">
            <v-list-item-content>
              <v-list-item-title class="font-weight-medium">{{ k.description || "ohne Beschreibung" }}</v-list-item-title>
              <v-list-item-subtitle>{{ k.roleText }} · angelegt {{ k.created }}</v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action class="flex-row align-center">
              <v-btn icon title="Link kopieren" aria-label="Link kopieren" @click="copy(k)"><v-icon>mdi-content-copy</v-icon></v-btn>
              <v-btn icon title="Per WhatsApp schicken" aria-label="Per WhatsApp schicken" :href="whatsapp(k)" target="_blank" rel="noopener"><v-icon>mdi-whatsapp</v-icon></v-btn>
              <v-btn icon color="error" title="Löschen" aria-label="Einladung löschen" @click="remove(k)"><v-icon>mdi-delete-outline</v-icon></v-btn>
            </v-list-item-action>
          </v-list-item>
        </template>
      </v-list>
    </v-card>

    <v-expansion-panels flat class="mt-4">
      <v-expansion-panel>
        <v-expansion-panel-header class="text-body-2 px-0">Adresse im Link: {{ address }}</v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-text-field
            v-model="hostOverride"
            label="Server-Adresse für Links (leer = automatisch)"
            :placeholder="autoHost"
            outlined
            dense
            hint="Nur ändern, wenn deine Leute den Server über einen Namen statt der IP erreichen."
            persistent-hint
            @change="saveHost"
          ></v-text-field>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- Neue Einladung -->
    <v-dialog v-model="create.open" max-width="520" scrollable>
      <v-card>
        <v-card-title>Neue Einladung</v-card-title>
        <v-card-text>
          <v-text-field v-model="create.description" label="Für wen? (z. B. Name)" outlined dense maxlength="80" autofocus></v-text-field>
          <v-radio-group v-model="create.kind" row class="mt-0">
            <v-radio label="Rolle auf dem Server" value="server"></v-radio>
            <v-radio label="Rolle in einem Kanal" value="channel"></v-radio>
          </v-radio-group>
          <template v-if="create.kind === 'server'">
            <v-select v-model="create.sgid" :items="serverRoleItems" label="Welche Rolle?" outlined dense></v-select>
            <p class="text-caption">Server Admin gibt es nicht als Einladung. Den gibst du direkt bei der Person.</p>
          </template>
          <template v-else>
            <v-autocomplete v-model="create.cid" :items="channelItems" item-text="name" item-value="cid" label="Welcher Kanal?" outlined dense no-data-text="Kein Kanal gefunden"></v-autocomplete>
            <v-select v-model="create.role" :items="channelRoleItems" label="Welche Rolle?" outlined dense></v-select>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="create.open = false">Abbrechen</v-btn>
          <v-btn color="primary" depressed :loading="busy" :disabled="!canCreate" @click="createKey">Link erzeugen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Ergebnis -->
    <v-dialog v-model="result.open" max-width="520">
      <v-card v-if="result.key">
        <v-card-title>Einladung bereit</v-card-title>
        <v-card-text>
          <p class="text-body-2">Schick den Link an {{ result.key.description || "die Person" }}. Ein Klick öffnet TeamSpeak, verbindet und gibt die Rolle: {{ result.key.roleText }}.</p>
          <v-text-field :value="link(result.key)" readonly outlined dense label="Link"></v-text-field>
        </v-card-text>
        <v-card-actions class="flex-wrap">
          <v-btn text @click="copy(result.key)"><v-icon left>mdi-content-copy</v-icon>Kopieren</v-btn>
          <v-btn text color="success" :href="whatsapp(result.key)" target="_blank" rel="noopener"><v-icon left>mdi-whatsapp</v-icon>WhatsApp</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" depressed @click="result.open = false">Fertig</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { ensureServer, asList, resolveGroups, channelPaths, ensureKanalChef, formatDate, errorText } from "@/iconic/rechte";

const HOST_KEY = "iconic.ts3.linkHost";
const readHost = () => {
  try {
    return localStorage.getItem(HOST_KEY) || "";
  } catch (e) {
    return "";
  }
};

export default {
  data() {
    return {
      loading: true,
      busy: false,
      keys: [],
      groups: { serverRoles: [], channelRoles: [], extraRoles: [] },
      channels: [],
      serverInfo: {},
      hostOverride: readHost(),
      create: { open: false, description: "", kind: "server", sgid: null, cid: null, role: "chef" },
      result: { open: false, key: null },
    };
  },
  computed: {
    autoHost() {
      const ips = this.serverInfo.virtualserverIp;
      const ip = Array.isArray(ips) ? ips[0] : ips;
      return ip && ip !== "0.0.0.0" ? ip : window.location.hostname.replace(/^ts-admin\./, "");
    },
    host() {
      return String(this.hostOverride || "").trim() || this.autoHost;
    },
    port() {
      return Number(this.serverInfo.virtualserverPort) || 9987;
    },
    address() {
      return this.port === 9987 ? this.host : `${this.host}:${this.port}`;
    },
    serverRoleItems() {
      const normal = this.groups.serverRoles.filter((r) => r.key === "normal").map((r) => ({ text: r.label, value: r.sgid }));
      const extra = this.groups.extraRoles.filter((r) => !r.locked).map((r) => ({ text: r.name, value: r.sgid }));
      return normal.concat(extra);
    },
    channelItems() {
      return channelPaths(this.channels).map((c) => ({ cid: c.cid, name: c.path }));
    },
    channelRoleItems() {
      return this.groups.channelRoles.map((r) => ({ text: r.label, value: r.key }));
    },
    canCreate() {
      if (this.create.kind === "server") return Boolean(this.create.sgid);
      return Boolean(this.create.cid && this.create.role);
    },
  },
  methods: {
    saveHost() {
      try {
        localStorage.setItem(HOST_KEY, String(this.hostOverride || "").trim());
      } catch (e) {
        /* nur Komfort */
      }
    },
    link(k) {
      const port = this.port === 9987 ? "" : `port=${this.port}&`;
      return `ts3server://${this.host}?${port}token=${encodeURIComponent(k.token)}`;
    },
    whatsapp(k) {
      const text = `Dein Zugang für unseren TeamSpeak «${this.serverInfo.virtualserverName || ""}» (${k.roleText}): ${this.link(k)}\nEinmal antippen, TeamSpeak muss installiert sein.`;
      return `https://wa.me/?text=${encodeURIComponent(text)}`;
    },
    copy(k) {
      this.$clipboard(this.link(k));
      this.$toast.success("Link kopiert.");
    },
    roleText(k) {
      if (String(k.tokenType) === "0") {
        const all = this.groups.serverRoles.map((r) => ({ id: r.sgid, n: r.label })).concat(this.groups.extraRoles.map((r) => ({ id: r.sgid, n: r.name })));
        const hit = all.find((r) => r.id === String(k.tokenId1));
        return hit ? hit.n : this.groups.sgName(k.tokenId1) || `Server-Gruppe ${k.tokenId1}`;
      }
      const role = this.groups.channelRoles.find((r) => r.cgid === String(k.tokenId1));
      const ch = channelPaths(this.channels).find((c) => c.cid === String(k.tokenId2));
      return `${role ? role.label : this.groups.cgName(k.tokenId1) || "Kanal-Rolle"} in ${ch ? ch.path : "Kanal " + k.tokenId2}`;
    },
    async load() {
      this.loading = true;
      try {
        await ensureServer();
        const ts = this.$TeamSpeak;
        const [serverInfo, serverGroups, channelGroups, channels, keys] = await Promise.all([
          ts.execute("serverinfo").then((r) => asList(r)[0]),
          ts.execute("servergrouplist").then(asList),
          ts.execute("channelgrouplist").then(asList),
          ts.execute("channellist").then(asList),
          ts.execute("privilegekeylist").then(asList),
        ]);
        this.serverInfo = serverInfo;
        this.groups = resolveGroups(serverInfo, serverGroups, channelGroups);
        this.channels = channels;
        this.keys = keys.map((k) => ({
          ...k,
          description: k.tokenDescription || "",
          created: formatDate(k.tokenCreated),
          roleText: this.roleText(k),
        }));
      } catch (err) {
        this.$toast.error(errorText(err));
      }
      this.loading = false;
    },
    openCreate() {
      const normal = this.groups.serverRoles.find((r) => r.key === "normal");
      this.create = { open: true, description: "", kind: "server", sgid: normal ? normal.sgid : null, cid: null, role: "chef" };
    },
    async createKey() {
      const c = this.create;
      this.busy = true;
      try {
        let params;
        if (c.kind === "server") {
          params = { tokentype: 0, tokenid1: c.sgid, tokenid2: 0 };
        } else {
          const role = this.groups.channelRoles.find((r) => r.key === c.role);
          const cgid = c.role === "chef" ? await ensureKanalChef(this.groups) : role.cgid;
          params = { tokentype: 1, tokenid1: cgid, tokenid2: c.cid };
        }
        if (c.description) params.tokendescription = c.description;
        const r = asList(await this.$TeamSpeak.execute("privilegekeyadd", params))[0] || {};
        this.create.open = false;
        await this.load();
        const key = this.keys.find((k) => k.token === r.token) || { token: r.token, description: c.description, roleText: "" };
        this.result = { open: true, key };
      } catch (err) {
        this.$toast.error(errorText(err));
      }
      this.busy = false;
    },
    async remove(k) {
      try {
        await this.$TeamSpeak.execute("privilegekeydelete", { token: k.token });
        this.$toast.success("Einladung gelöscht.");
      } catch (err) {
        this.$toast.error(errorText(err));
      }
      await this.load();
    },
  },
  created() {
    this.load();
  },
};
</script>

<style scoped>
.einladungen {
  max-width: 880px;
}
</style>
